import { IFullAddress } from '@/models/addresses.model';
import { FileVariant, IFile } from '@/models/file.model';
import { IPaginationDTO, IPaginationResponse } from '@/models/pagination.model';
import { StepType } from '@/models/step.model';
import { Dayjs } from 'dayjs';
import { IPropertyFile } from '@/models/documents.model';

export enum OrdersEndpoints {
  ROOT = '/orders',
  LIST = '/list',
  STATUS = '/status',
  COMPLETE = '/complete',
  FILES = '/files',
  APPRAISAL = '/appraisal',
}

export const staticOrderEndpoints = {
  GET_ORDERS: `${OrdersEndpoints.ROOT}${OrdersEndpoints.LIST}`,
};

export const getOrdersEndpoints = ({ orderId, fileId }: { orderId: string; fileId?: string }) => ({
  GET_ORDER: `${OrdersEndpoints.ROOT}/${orderId}`,
  CHANGE_STATUS: `${OrdersEndpoints.ROOT}/${orderId}${OrdersEndpoints.STATUS}`,
  COMPLETE_ORDER: `${OrdersEndpoints.ROOT}/${orderId}${OrdersEndpoints.COMPLETE}`,
  UPLOAD_FILES: `${OrdersEndpoints.ROOT}/${orderId}${OrdersEndpoints.FILES}`,
  UPDATE_APPRAISAL_ORDER: `${OrdersEndpoints.ROOT}/${orderId}${OrdersEndpoints.APPRAISAL}`,
  DELETE_FILES: `${OrdersEndpoints.ROOT}/${orderId}${OrdersEndpoints.FILES}/${fileId}`,
});

export enum OrderStatus {
  NEW = 'new',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELED = 'canceled',
}

export enum OrderType {
  APPRAISER = 'appraiser',
  PHOTOSHOOTING = 'photoshooting',
  DOCUMENTS = 'documents',
  AD = 'ad',
  VIEW = 'view',
  BIDDING = 'bidding',
  CONTRACT = 'contract',
  SETTLEMENT = 'settlement',
  SERVICE_VIEWING_PACKAGE = 'service_viewing_package',
}

export interface IAppraisal {
  condition_report_created_at?: string | Dayjs;
  rate_report_created_at?: string | Dayjs;
  price?: number;
  files?: IFile[];
  [FileVariant.FLOOR_PLAN]?: IFile[];
}

export enum PaymentStatus {
  INITIALIZED = 'initialized',
  AUTHORIZED = 'authorized',
  PAID = 'paid',
  FAILED = 'failed',
  REFUNDED = 'refunded',
  CANCELLED = 'cancelled',
  EXPIRED = 'expired',
}

export interface ICompanyAdditionalOfferings {
  PK: string;
  price: number;
  name_no: string;
  name_en: string;
  description_no: string;
  description_en: string;
}

export enum PaymentMethod {
  CARD = 'card',
  VIPPS = 'vipps',
  INSTALLMENT_ACCOUNT = 'installment_account',
  INVOICE = 'invoice',
}

export enum PricePackageBundleType {
  ESSENTIAL = 'ESSENTIAL',
  ESSENTIAL_PLUS_CONDOMINIUM = 'ESSENTIAL_PLUS_CONDOMINIUM',
  ESSENTIAL_PLUS_HOUSING_ASSOCIATION = 'ESSENTIAL_PLUS_HOUSING_ASSOCIATION',
  WITH_0_SALES_REPORTS = 'WITH_0_SALES_REPORTS',
  WITH_10_SALES_REPORTS = 'WITH_10_SALES_REPORTS',
  WITH_15_SALES_REPORTS = 'WITH_15_SALES_REPORTS',
  WITH_20_SALES_REPORTS = 'WITH_20_SALES_REPORTS',
  WITH_30_SALES_REPORTS = 'WITH_30_SALES_REPORTS',
}

export interface IOrder {
  PK: string;
  SK: string;
  user_id: string;
  property_id: string;
  step_name: OrderType;
  status: OrderStatus;
  payment_status: PaymentStatus;
  payment_instrument: PaymentMethod;
  selected_bundle: PricePackageBundleType;
  additional_offerings?: ICompanyAdditionalOfferings[];
  user_name: string;
  property_address: IFullAddress;
  created: string;
  modified: string;
  tripletex_order_number: string;
  price: number;
  appraisal?: IAppraisal;
  files: IPropertyFile[];
  [FileVariant.PHOTOS_INSIDE_PROPERTY]?: IFile[];
  [FileVariant.PHOTOS_OUTSIDE_PROPERTY]?: IFile[];
  [FileVariant.FLOOR_PLAN]?: IFile[];
}

export const ordersWithPayment = new Set([
  OrderType.APPRAISER,
  OrderType.DOCUMENTS,
  OrderType.PHOTOSHOOTING,
  OrderType.AD,
  OrderType.SERVICE_VIEWING_PACKAGE,
]);

export const ordersWithPackages = new Set([OrderType.DOCUMENTS, OrderType.SERVICE_VIEWING_PACKAGE]);

export const ordersWithAdditionalOfferings = new Set([OrderType.APPRAISER, OrderType.PHOTOSHOOTING]);

// ----------------- DTOs ----------------

export interface IGetOrdersDTO extends IPaginationDTO {
  address?: string;
  user?: string;
  // payment_method?: PaymentMethod;
  payment_status?: PaymentStatus;
  step_name?: StepType;
  status?: OrderStatus;
}

export interface IChangeOrderStatusDTO {
  status: OrderStatus;
  id: string;
}

export interface ICompleteOrderDTO {
  id: string;
}

export interface IUpdateAppraisalOrderDTO {
  id: string;
  condition_report_created_at: string;
  rate_report_created_at: string;
  price: number;
}

export interface IUploadFilesDTO {
  orderId: string;
  files: IFile[];
  type: FileVariant;
}

export interface IDeleteFilesDTO {
  orderId: string;
  fileId: string;
}

// ------------ Responses --------------

export interface IGetOrdersResponse {
  items: IOrder[];
  cursor: IPaginationResponse;
}

export type IGetOrderResponse = IOrder;

export type TChangeOrderStatusResponse = IOrder;

export type TCompleteOrderResponse = IOrder;

export type TUploadFilesResponse = IOrder;

export type TDeleteFilesResponse = IOrder;
